/**
 * FindingPolarityMark — the big colored block on the left edge of a
 * finding row. Two-signal scan anchor:
 *
 *   Positive findings (strengths) — solid green.
 *   Negative findings (issues, gaps, incorrect) — traffic-light
 *     severity scale: red (critical) → orange (major) → yellow (minor)
 *     → pale (observation).
 *   Off-topic — muted gray.
 *
 * Polarity is also carried by the kicker word color (ISSUE red, GAP
 * amber, STRENGTH green), so the block doesn't need to encode both
 * axes. It's a severity-weighted polarity mark: "is this good, or
 * how urgent is it?"
 */

import { cn } from "@/lib/utils";
import type { FindingType, Severity } from "./vocabulary";

type MarkKind = "positive" | "negative" | "muted";

const typeKindMap: Record<FindingType, MarkKind> = {
  helps: "positive",
  hinders: "negative",
  missing: "negative",
  incorrect: "negative",
  irrelevant: "muted",
};

const polarityLabel: Record<FindingType, string> = {
  helps: "Strength",
  hinders: "Issue",
  missing: "Gap",
  incorrect: "Incorrect",
  irrelevant: "Off-topic",
};

/**
 * Severity → bg class for negative findings. Traffic-light scale.
 * Tailwind needs the full class as a literal, so the table is explicit.
 */
const negativeSeverityBgMap: Record<Severity, string> = {
  critical: "bg-mark-critical",
  major: "bg-mark-important",
  minor: "bg-mark-minor",
  nit: "bg-mark-minor/45",
};

interface FindingPolarityMarkProps {
  type: FindingType | string;
  severity?: Severity | string | null;
  /** "md" = 6px wide (narrow bar), "lg" = 12px wide */
  size?: "md" | "lg";
  className?: string;
}

const sizeClasses = {
  md: "w-1.5",
  lg: "w-3",
} as const;

function resolveKind(type: FindingType | string): MarkKind {
  return typeKindMap[type as FindingType] ?? "muted";
}

function resolveSeverity(severity?: Severity | string | null): Severity {
  if (
    severity === "critical" ||
    severity === "major" ||
    severity === "minor" ||
    severity === "nit"
  ) {
    return severity;
  }
  return "nit";
}

function resolveBg(kind: MarkKind, severity: Severity): string {
  if (kind === "positive") return "bg-mark-helps";
  if (kind === "muted") return "bg-muted-foreground/30";
  return negativeSeverityBgMap[severity];
}

export function FindingPolarityMark({
  type,
  severity,
  size = "md",
  className,
}: FindingPolarityMarkProps) {
  const kind = resolveKind(type);
  const sev = resolveSeverity(severity);
  const bg = resolveBg(kind, sev);
  const label = polarityLabel[type as FindingType] ?? polarityLabel.irrelevant;

  return (
    <div
      role="img"
      aria-label={label}
      className={cn(
        "shrink-0 self-stretch rounded-sm",
        bg,
        sizeClasses[size],
        className,
      )}
    />
  );
}
